import { useEffect, useState, useCallback } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Eye, Plus, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const MAX_CONSULTAZIONE = 2;

export function ConsultazioneUsersSection({ isUnlimited }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ nome: "", cognome: "", email: "", username: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!isUnlimited) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/consultazione-users");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [isUnlimited]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => setForm({ nome: "", cognome: "", email: "", username: "", password: "" });

  const create = async () => {
    if (!form.nome || !form.cognome || !form.email || !form.username || !form.password) {
      toast.error("Tutti i campi sono obbligatori");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password troppo corta (min 8 caratteri)");
      return;
    }
    setSaving(true);
    try {
      await api.post("/consultazione-users", form);
      toast.success("Utenza di consultazione creata");
      resetForm();
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleStato = async (u) => {
    const nuovo = u.stato === "attivo" ? "disattivo" : "attivo";
    try {
      await api.patch(`/consultazione-users/${u.id}`, { stato: nuovo });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`Nuova password per ${u.username} (min 8 caratteri):`);
    if (!pw) return;
    if (pw.length < 8) { toast.error("Password troppo corta"); return; }
    try {
      await api.patch(`/consultazione-users/${u.id}`, { password: pw });
      toast.success("Password aggiornata");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare l'utenza ${u.username}?`)) return;
    try {
      await api.delete(`/consultazione-users/${u.id}`);
      toast.success("Utenza eliminata");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Card className="p-6 border-slate-200" data-testid="consultazione-section">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
          <Eye className="w-4 h-4 text-amber-700" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-semibold">Utenze di consultazione</h2>
          <p className="text-sm text-slate-600">Accesso in sola lettura a foglio cassa, sospesi, versamenti e statistiche. Non vedono Impostazioni né Abbonamento. Massimo {MAX_CONSULTAZIONE}.</p>
        </div>
      </div>

      {!isUnlimited ? (
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="consultazione-plan-required">
          Funzionalità disponibile solo con il piano <b>Unlimited</b>. Passa al piano superiore dalla sezione Abbonamento per abilitare le utenze di consultazione.
        </div>
      ) : loading ? (
        <div className="text-sm text-slate-500">Caricamento…</div>
      ) : (
        <>
          {items.length === 0 && !showForm && (
            <div className="text-sm text-slate-500 mb-3">Nessuna utenza di consultazione creata.</div>
          )}
          {items.length > 0 && (
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 mb-3">
              {items.map((u) => (
                <div key={u.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`consultazione-row-${u.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{u.nome} {u.cognome} <span className="text-slate-400">— @{u.username}</span></div>
                    <div className="text-xs text-slate-500 truncate">{u.email}</div>
                  </div>
                  <Badge variant="outline" className={u.stato === "attivo" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}>
                    {u.stato === "attivo" ? "Attivo" : "Disattivato"}
                  </Badge>
                  <div className="flex items-center gap-1.5">
                    <Switch checked={u.stato === "attivo"} onCheckedChange={() => toggleStato(u)} data-testid={`consultazione-toggle-${u.id}`} />
                    <Button size="sm" variant="outline" onClick={() => resetPassword(u)} data-testid={`consultazione-pwd-${u.id}`}>
                      <KeyRound className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => remove(u)} className="text-red-600 hover:bg-red-50" data-testid={`consultazione-del-${u.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showForm ? (
            <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 space-y-3" data-testid="consultazione-form">
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Nome</Label><Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="cons-form-nome" /></div>
                <div><Label>Cognome</Label><Input value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} data-testid="cons-form-cognome" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cons-form-email" /></div>
                <div><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="cons-form-username" /></div>
                <div className="md:col-span-2"><Label>Password (min 8 caratteri)</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="cons-form-password" /></div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>Annulla</Button>
                <Button onClick={create} disabled={saving} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="cons-form-submit">
                  {saving ? "Creazione…" : "Crea utenza"}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={() => setShowForm(true)}
              disabled={items.length >= MAX_CONSULTAZIONE}
              data-testid="consultazione-add-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {items.length >= MAX_CONSULTAZIONE ? `Massimo ${MAX_CONSULTAZIONE} raggiunto` : "Aggiungi utenza consultazione"}
            </Button>
          )}
        </>
      )}
    </Card>
  );
}
